/**
 * Phase 8 T-24 §28.B — Bulk compose floating panel.
 *
 * Slides up from the bottom of the canvas when ≥ 2 rows are selected in
 * any Airtable-style table (Candidates, Applications, Jobs).
 *
 * Anatomy:
 *   Header   : "Compose Message" · pager ‹ 1 of N › · ×
 *   To chips : up to 3 visible + "+N more"
 *   Subject  : text input
 *   Body     : textarea (merge variable tokens rendered as blue chips)
 *   Format   : B / I / U / link / align / list icon row
 *   Templates: up to 3 recent templates
 *   Footer   : [/Variable] · [Sequences] · Group edit [toggle] · [Send N emails]
 *
 * Closing with a non-empty draft prompts "Discard draft?".
 * Merge variables are highlighted bg-blue-100 text-blue-900.
 *
 * C-28 — Send is no longer a toast stub: it enqueues one
 * `ggj_b2b_outbox_events` row per candidate via `emailService.sendBulk`.
 * Actual delivery still happens server-side via the existing outbox worker.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Bold,
  ChevronLeft,
  ChevronRight,
  Italic,
  LayoutList,
  Link2,
  Loader2,
  AlignLeft,
  Underline,
  X,
} from 'lucide-react';
import { useGlobalToast } from '@shared/components/Toast/ToastProvider';
import { useAuth } from '@core/auth/useAuth';
import { emailService } from '@core/supabase/services/emailService';

export interface BulkComposeRecipient {
  id: string;
  name: string;
  email?: string | null;
}

export interface BulkComposePanelProps {
  recipients: BulkComposeRecipient[];
  onClose: () => void;
}

const TEMPLATES = [
  { id: 't1', label: 'Intro outreach', body: 'Hi {{first_name}}, I came across your profile…' },
  { id: 't2', label: 'Interview invite', body: "Hi {{first_name}}, I'd love to schedule an interview…" },
  { id: 't3', label: 'Follow-up', body: "Hi {{first_name}}, just following up on my earlier message…" },
];

const VARIABLE_CHIP_RE = /\{\{([^}]+)\}\}/g;

/**
 * Render body text with {{variable}} tokens highlighted as blue inline chips.
 * Used in the preview area only — the textarea itself is plain text.
 */
function BodyPreview({ body }: { body: string }) {
  const parts: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  VARIABLE_CHIP_RE.lastIndex = 0;
  while ((m = VARIABLE_CHIP_RE.exec(body)) !== null) {
    if (m.index > last) parts.push(<span key={last}>{body.slice(last, m.index)}</span>);
    parts.push(
      <span
        key={m.index}
        className="inline-block rounded px-1 text-[12px] font-medium bg-blue-100 text-blue-900"
      >
        {`{{${m[1]}}}`}
      </span>,
    );
    last = m.index + m[0].length;
  }
  if (last < body.length) parts.push(<span key={last}>{body.slice(last)}</span>);
  return <>{parts}</>;
}

export function BulkComposePanel({ recipients, onClose }: BulkComposePanelProps) {
  const [previewIdx, setPreviewIdx] = useState(0);
  const [groupEdit, setGroupEdit] = useState(true);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { showToast } = useGlobalToast();
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';

  const n = recipients.length;
  const recipient = recipients[previewIdx] ?? recipients[0];

  const isDirty = subject.trim().length > 0 || body.trim().length > 0;

  const handleClose = useCallback(() => {
    if (isDirty) {
      setConfirmDiscard(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const handleDiscard = useCallback(() => {
    setConfirmDiscard(false);
    onClose();
  }, [onClose]);

  const handleSend = useCallback(async () => {
    if (isSending) return;
    if (!orgId) {
      showToast({ title: 'Sign in to send emails.', variant: 'error' });
      return;
    }
    const candidateIds = Array.from(
      new Set(recipients.map((r) => r.id).filter((id): id is string => Boolean(id))),
    );
    if (candidateIds.length === 0) {
      showToast({ title: 'No recipients selected.', variant: 'error' });
      return;
    }

    setIsSending(true);
    try {
      const { queued } = await emailService.sendBulk({
        orgId,
        candidateIds,
        // §28.B panel doesn't yet bind to a persisted template row — record
        // the last-applied preset id, or 'custom' for a free-form draft so
        // the outbox worker can branch on body/subject merge variables.
        templateId: activeTemplateId ?? 'custom',
        variables: { subject, body },
      });
      showToast({
        title: `${queued} email${queued !== 1 ? 's' : ''} queued`,
        variant: 'success',
      });
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not queue emails.';
      showToast({ title: 'Send failed', body: message, variant: 'error' });
    } finally {
      setIsSending(false);
    }
  }, [activeTemplateId, body, isSending, onClose, orgId, recipients, showToast, subject]);

  const handleSequences = useCallback(() => {
    showToast({ title: 'Sequence attach coming soon.', variant: 'info' });
  }, [showToast]);

  const insertVariable = useCallback((varName: string) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart ?? body.length;
    const end = ta.selectionEnd ?? body.length;
    const token = `{{${varName}}}`;
    setBody((b) => b.slice(0, start) + token + b.slice(end));
    window.requestAnimationFrame(() => {
      ta.selectionStart = start + token.length;
      ta.selectionEnd = start + token.length;
      ta.focus();
    });
  }, [body.length]);

  const insertTemplate = useCallback((tmpl: (typeof TEMPLATES)[number]) => {
    setBody((b) => (b ? `${b}\n\n${tmpl.body}` : tmpl.body));
    setActiveTemplateId(tmpl.id);
    textareaRef.current?.focus();
  }, []);

  // Auto-grow textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [body]);

  return (
    <>
      {/* Discard confirm dialog */}
      {confirmDiscard ? (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40">
          <div
            className="w-[320px] rounded-[10px] border p-5 shadow-xl"
            style={{
              background: 'var(--vscode-bg-elevated)',
              borderColor: 'var(--vscode-border-primary)',
            }}
          >
            <p className="mb-1 text-[14px] font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>
              Discard draft?
            </p>
            <p className="mb-4 text-[13px]" style={{ color: 'var(--vscode-text-secondary)' }}>
              Your unsaved message will be lost.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDiscard(false)}
                className="h-8 rounded-[8px] border px-3 text-[13px]"
                style={{ borderColor: 'var(--vscode-border-primary)', color: 'var(--vscode-text-secondary)' }}
              >
                Keep editing
              </button>
              <button
                type="button"
                onClick={handleDiscard}
                className="h-8 rounded-[8px] bg-red-500/15 px-3 text-[13px] font-medium text-red-600 dark:text-red-300"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Sliding panel */}
      <div
        role="dialog"
        aria-modal="false"
        aria-label="Bulk compose"
        className="fixed bottom-4 left-1/2 z-[250] w-[720px] max-w-[calc(100vw-32px)] -translate-x-1/2 rounded-[10px] border shadow-lg"
        style={{
          background: 'var(--vscode-bg-elevated)',
          borderColor: 'var(--vscode-border-primary)',
          // 380 px height from spec
          maxHeight: 380,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          className="flex h-10 shrink-0 items-center gap-3 border-b px-4"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          <span className="flex-1 text-[13px] font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>
            Compose Message
          </span>
          {/* Pager */}
          <div className="flex items-center gap-1 text-[12px]" style={{ color: 'var(--vscode-text-secondary)' }}>
            <button
              type="button"
              disabled={previewIdx === 0}
              onClick={() => setPreviewIdx((i) => Math.max(0, i - 1))}
              aria-label="Previous recipient"
              className="rounded p-0.5 hover:bg-[var(--vscode-bg-hover)] disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
            </button>
            <span>
              {previewIdx + 1} of {n}
            </span>
            <button
              type="button"
              disabled={previewIdx === n - 1}
              onClick={() => setPreviewIdx((i) => Math.min(n - 1, i + 1))}
              aria-label="Next recipient"
              className="rounded p-0.5 hover:bg-[var(--vscode-bg-hover)] disabled:opacity-40"
            >
              <ChevronRight className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSending}
            aria-label="Close compose panel"
            className="rounded-[8px] p-1.5 hover:bg-[var(--vscode-bg-hover)] disabled:cursor-not-allowed disabled:opacity-40"
            style={{ color: 'var(--vscode-text-tertiary)' }}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {/* To: chips */}
          <div className="flex flex-wrap items-center gap-1.5 text-[12px]">
            <span className="text-[12px] font-medium" style={{ color: 'var(--vscode-text-tertiary)' }}>
              To:
            </span>
            {recipients.slice(0, 3).map((r) => (
              <span
                key={r.id}
                className="inline-flex h-6 items-center rounded-full border px-2.5 text-[12px]"
                style={{
                  borderColor: 'var(--vscode-border-primary)',
                  color: 'var(--vscode-text-secondary)',
                  background: 'var(--vscode-bg-secondary)',
                }}
              >
                {r.name || r.email || r.id}
              </span>
            ))}
            {n > 3 ? (
              <span className="text-[12px]" style={{ color: 'var(--vscode-text-tertiary)' }}>
                +{n - 3} more
              </span>
            ) : null}
            {!groupEdit && recipient ? (
              <span className="ml-2 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                Previewing: {recipient.name || recipient.email}
              </span>
            ) : null}
          </div>

          {/* Subject */}
          <input
            type="text"
            placeholder="Subject…"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full rounded-[8px] border bg-transparent px-3 py-1.5 text-[13px] outline-none focus:border-emerald-500"
            style={{ borderColor: 'var(--vscode-border-primary)', color: 'var(--vscode-text-primary)' }}
            aria-label="Subject"
          />

          {/* Body */}
          <div
            className="rounded-[8px] border"
            style={{ borderColor: 'var(--vscode-border-primary)' }}
          >
            <textarea
              ref={textareaRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your message… use {{first_name}}, {{role}}, {{company}} for merge variables."
              rows={3}
              className="min-h-[72px] w-full resize-none rounded-t-[8px] bg-transparent px-3 py-2 text-[13px] outline-none placeholder:text-[color:var(--vscode-text-tertiary)]"
              style={{ color: 'var(--vscode-text-primary)', maxHeight: 160 }}
              aria-label="Message body"
            />
            {/* Formatting toolbar */}
            <div
              className="flex items-center gap-1 border-t px-2 py-1"
              style={{ borderColor: 'var(--vscode-border-primary)' }}
            >
              {[Bold, Italic, Underline, Link2, AlignLeft, LayoutList].map((Icon, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={['Bold', 'Italic', 'Underline', 'Link', 'Align', 'List'][i]}
                  className="rounded p-1 hover:bg-[var(--vscode-bg-hover)]"
                  style={{ color: 'var(--vscode-text-tertiary)' }}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden />
                </button>
              ))}
            </div>
          </div>

          {/* Merge variable preview badge */}
          {body.includes('{{') ? (
            <div className="text-[12px]" style={{ color: 'var(--vscode-text-secondary)' }}>
              <span className="mr-1 font-medium">Preview:</span>
              <BodyPreview body={body} />
            </div>
          ) : null}

          {/* Templates */}
          <div>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--vscode-text-tertiary)' }}>
              Templates
            </p>
            <div className="flex flex-wrap gap-2">
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => insertTemplate(t)}
                  className="h-7 rounded-[8px] border px-2.5 text-[12px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
                  style={{
                    borderColor: 'var(--vscode-border-primary)',
                    color: 'var(--vscode-text-secondary)',
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex shrink-0 flex-wrap items-center gap-2 border-t px-4 py-2"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          {/* /Variable inserter */}
          <div className="relative">
            <VariableInserter onInsert={insertVariable} />
          </div>

          {/* Sequences */}
          <button
            type="button"
            onClick={handleSequences}
            className="h-7 rounded-[8px] border px-2.5 text-[12px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
            style={{ borderColor: 'var(--vscode-border-primary)', color: 'var(--vscode-text-secondary)' }}
          >
            Sequences
          </button>

          {/* Group edit toggle */}
          <button
            type="button"
            role="switch"
            aria-checked={groupEdit}
            onClick={() => setGroupEdit((v) => !v)}
            className="flex h-7 items-center gap-1.5 rounded-[8px] border px-2.5 text-[12px] transition-colors"
            style={{
              borderColor: groupEdit ? '#10B981' : 'var(--vscode-border-primary)',
              background: groupEdit ? '#10B98115' : undefined,
              color: groupEdit ? '#065F46' : 'var(--vscode-text-secondary)',
            }}
          >
            <span
              className="inline-block h-3.5 w-6 rounded-full transition-colors"
              style={{ background: groupEdit ? '#10B981' : 'var(--vscode-bg-tertiary)' }}
            >
              <span
                className="block h-3.5 w-3.5 translate-y-0 rounded-full bg-white shadow transition-transform"
                style={{ transform: `translateX(${groupEdit ? '10px' : '0px'})` }}
              />
            </span>
            Group edit
          </button>

          <div className="ml-auto">
            <button
              type="button"
              disabled={(!body.trim() && !subject.trim()) || isSending}
              onClick={() => void handleSend()}
              aria-busy={isSending}
              className="inline-flex h-8 items-center gap-1.5 rounded-[10px] bg-emerald-500 px-4 text-[13px] font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  Queueing…
                </>
              ) : (
                <>
                  Send {n} email{n !== 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Variable inserter ────────────────────────────────────────────────────────

const COMMON_VARS = ['first_name', 'last_name', 'role', 'company', 'sender_name'];

function VariableInserter({ onInsert }: { onInsert: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="h-7 rounded-[8px] border px-2.5 text-[12px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
        style={{ borderColor: 'var(--vscode-border-primary)', color: 'var(--vscode-text-secondary)' }}
      >
        / Variable
      </button>
      {open ? (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden />
          <div
            className="absolute bottom-full left-0 z-20 mb-1 min-w-[160px] rounded-[8px] border py-1 shadow-lg"
            style={{ background: 'var(--vscode-bg-elevated)', borderColor: 'var(--vscode-border-primary)' }}
          >
            {COMMON_VARS.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => { onInsert(v); setOpen(false); }}
                className="flex w-full items-center px-3 py-1.5 text-left text-[12px] hover:bg-[var(--vscode-bg-hover)]"
                style={{ color: 'var(--vscode-text-secondary)' }}
              >
                <span className="rounded bg-blue-100 px-1 text-blue-900 mr-2">{`{{${v}}}`}</span>
              </button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
